import { createApp, reactive, ref, watch } from 'vue/dist/vue.esm-browser';
import bigDecimal from 'js-big-decimal';

const calculators = document.querySelectorAll( '.hpu-calculator' );
calculators.forEach( ( calculator ) => {
	// fetch data to pass to vue model
	let products = [];
	const datasource = calculator.getAttribute( 'data-source' );
	fetch( `/api/sitecore/calculators/FetchHPUCalculatorData?datasourceID=${datasource}` )
		.then( ( d ) => d.json() )
		.then( ( response ) => {
			if( response.products ) {
				products = response.products;
				const combinableProducts = products.filter( ( product ) => product.combinable );
				const combinableProductsByModelFamily = groupBy( combinableProducts, 'modelFamily' );
				const productsByModelFamily = groupBy( products, 'modelFamily' );
				const data = {
					combinableProductsByModelFamily: combinableProductsByModelFamily,
					currencies: [
						{ text: 'BRL (R$)', value: 'BRL' },
						{ text: 'CAD ($)', value: 'CAD' },
						{ text: 'CNY (¥)', value: 'CNY' },
						{ text: 'EUR (€)', value: 'EUR' },
						{ text: 'GBP (£)', value: 'GBP' },
						{ text: 'INR (₹)', value: 'INR' },
						{ text: 'JPY (¥)', value: 'JPY' },
						{ text: 'KRW (₩)', value: 'KRW' },
						{ text: 'RUB (₽)', value: 'RUB' },
						{ text: 'USD ($)', value: 'USD' },
					],
					locations: [
						{ text: '60 Hz', value: '60' },
						{ text: '50 Hz', value: '50' }
					],
					modelFamilies: response.modelFamilyList,
					productsByModelFamily: productsByModelFamily,
					products: products,
					volumeUnits: [
						{ text: 'gallons', value: 'g' },
						{ text: 'liters', value: 'l' }
					]
				};
				initHpuCalculator( calculator, data );
			}
		} );
} );

const forms = document.querySelectorAll( '.hpu-calculator-quote-form form' );
forms.forEach( ( form ) => {
	const facilitySessionData = JSON.parse( window.sessionStorage.hpuSystem );
	if ( !facilitySessionData )
		return;

	populateForm( form, facilitySessionData );
} );

function addHiddenField( form, name, value ) {
	var input = document.createElement( 'input' );
	input.setAttribute( 'type', 'hidden' );
	input.setAttribute( 'name', name );
	input.setAttribute( 'value', value );
	form.appendChild( input );
}

/**
 * group array of items by a key
 * outputs object
 */
function groupBy( items, key ) {
	return items.reduce( ( result, item ) => {
		let groupKey = item[key];
		if ( !result[groupKey] )
			result[groupKey] = [];
		result[groupKey].push( item );
		return result;
	}, {} );
}

/**
* init hpu calculator vue app & mount
*/
function initHpuCalculator( calculator, data ) {
	const app = createApp( {
		setup() {
			// get url params for potential initial values
			const urlParams = new URLSearchParams( window.location.search );

			const initialProduct = data.products[0];
			const initialFacilityData = {
				capacity: 0,
				carbonAnnually: 0,
				carbonDaily: 0,
				carbonHourly: 0,
				carbonWeekly: 0,
				isFluidPowerSystemManaged : 'yes' === urlParams.get( 'fpsm' ) || false,
				powerCostAnnually: 0,
				powerCostDaily: 0,
				powerCostHourly: 0,
				powerCostWeekly: 0,
				powerUsedAnnually: 0,
				powerUsedDaily: 0,
				powerUsedHourly: 0,
				powerUsedWeekly: 0,
			};
			const initialRecommendationFacilityData = Object.assign( { name: 'recommendations facility'}, initialFacilityData );
			const initialRecommendationFacilityFMSData = Object.assign( { name: 'recommendations FMS facility'}, initialFacilityData );

			const currencyParam = urlParams.get( 'currency' );
			let initialCurrency = data.currencies[data.currencies.length - 1];
			if ( currencyParam ) {
				initialCurrency = data.currencies.find( c => c.value == currencyParam );
			}

			const urlParamsHasSystem = urlParams.has( 'system[]' );
			let systemUnits = [ { modelFamily: initialProduct.modelFamily, modelNumber: initialProduct.modelNumber } ];
			let initialProducts = [initialProduct];
			let initialDisplayAddButton = initialProduct.combinable;
			let initialAvailableModelFamilies = data.modelFamilies;
			if ( urlParamsHasSystem ) {
				const systemProductList = urlParams.getAll( 'system[]' );
				systemUnits = [];
				initialProducts = [];
				systemProductList.map( ( systemProduct ) => {
					systemUnits.push( { modelFamily: systemProduct.split( '.' )[0], modelNumber: systemProduct } );
					const product = data.products.find( ( p ) => p.modelNumber == systemProduct );
					initialProducts.push( product );
				} );
				if ( 7 < initialProducts.length )
					initialDisplayAddButton = false;
				if ( initialProducts.some( p => p.combinable ) ) {
					initialDisplayAddButton = true;
					initialAvailableModelFamilies = new Set( data.products.filter( ( product => product.combinable ) ).map( ( product ) => product.modelFamily ) );
				}
			}

			/** setup initial values **/
			let availableModelFamilies = ref( initialAvailableModelFamilies );
			let basePowerRateValue = ref( urlParams.get( 'electricityCost' ) || 0.2 );
			let currentStep = ref( 1 );
			let displayAddUnitButton = ref( initialDisplayAddButton );
			let facilityData = reactive( initialFacilityData );
			let fluidPowerSystemManaged = ref( urlParams.get( 'fpsm' ) || 'no' );
			let fluidPowerSystemSectionAvailable = ref( true );
			let gallonsText = ref( 'gallons' );
			let hasRecommendations = ref( false );
			let litersText = ref( 'liters' );
			let quoteAvailable = ref( true );
			let recommendationFacilityData = reactive( initialRecommendationFacilityData );
			let recommendationFacilityFMSData = reactive( initialRecommendationFacilityFMSData );
			let recommendedFacilityProducts = ref( [] );
			let recommendedProducts = ref( [{ quantity: 0, unit: initialProduct }] );
			let selectedCurrency = ref( initialCurrency );
			let selectedCurrencyValue = ref( currencyParam || data.currencies[data.currencies.length - 1].value );
			let selectedLocation = ref( urlParams.get( 'loc' ) || data.locations[0].value );
			let selectedProducts = ref( initialProducts );
			let selectedUnits = ref( systemUnits );
			let showContactMessage = ref ( false );
			let showFluidPowerSystemRecommendation = ref ( false );
			let costSliderValue = ref( urlParams.get( 'csv' ) || 80 );
			let savingsSliderValue = ref( urlParams.get( 'ssv' ) || 80 );
			let utilizationDaysValue = ref( urlParams.get( 'daysPerWeek' ) || 7 );
			let utilizationHoursValue = ref( urlParams.get( 'hoursPerDay' ) ||  8 );
			let waterRateValue = ref( urlParams.get( 'waterRateValue' ) || 1.5 );
			let waterVolumeUnit = ref( urlParams.get( 'waterVolumeUnit' ) || data.volumeUnits[0].value );
			let waterVolumeValue = ref( urlParams.get( 'waterVolumeValue' ) || 1000 );

			/**
				* adds new empty hpu to selected unit list
				*/
			const addNewUnit = () => {
				const last = selectedUnits.value.length - 1;
				selectedUnits.value.push( { modelFamily: selectedUnits.value[last].modelFamily, modelNumber: selectedUnits.value[last].modelNumber } );
			};

			/**
			 * calculate facility data
			 */
			const calculateFacilityData = ( facility, facilityUnits, useSavingsSlider = false ) => {
				let facilityCapacity = 0;
				let hourlyCarbon = new bigDecimal( 0 );
				let hourlyPowerUsed = new bigDecimal( 0 );
				let hourlyUnitPowerCost = new bigDecimal( 0 );
				let hourlyFacilityCoolingCost = new bigDecimal( 0 );
				let hourlyUnitCoolingCost = new bigDecimal( 0 );
				const hoursPerDay = new bigDecimal( utilizationHoursValue.value );
				const daysPerWeek = new bigDecimal( utilizationDaysValue.value );
				facilityUnits.forEach( ( product ) => {
					// compute facility capacity
					if( product.maxFlow ) {
						facilityCapacity += product.maxFlow;
					} else {
						const productMaxFlow = unitMaxFlow( product );
						facilityCapacity += productMaxFlow;
					}

					// compute facility hourly power consumption
					if( product.powerConsumed ) {
						hourlyPowerUsed = hourlyPowerUsed.add( new bigDecimal( product.powerConsumed ) );
					} else {
						const productPowerUsed = unitPowerConsumed( product, facility.isFluidPowerSystemManaged, useSavingsSlider );
						hourlyPowerUsed = hourlyPowerUsed.add( new bigDecimal( productPowerUsed ) );
					}

					if( 0 === product.waterConsumed )
						unitWaterConsumedPerHour( product );

					// compute hourly unit power cost
					if( 0 === product.unitPowerCost || 0 === product.facilityCost || 0 === product.waterCost ) {
						const updatedProduct = unitCostPerHour( product, facility.isFluidPowerSystemManaged, useSavingsSlider );
						hourlyUnitPowerCost = hourlyUnitPowerCost.add( new bigDecimal( updatedProduct.unitPowerCost ) );
						hourlyFacilityCoolingCost = hourlyFacilityCoolingCost.add( new bigDecimal( updatedProduct.facilityCost ) );
						hourlyUnitCoolingCost = hourlyUnitCoolingCost.add( new bigDecimal( updatedProduct.waterCost ) );
					} else {
						if( product.unitPowerCost && 0 < product.unitPowerCost )
							hourlyUnitPowerCost = hourlyUnitPowerCost.add( new bigDecimal( product.unitPowerCost ) );

						if( product.facilityCost )
							hourlyFacilityCoolingCost = hourlyFacilityCoolingCost.add( new bigDecimal( product.facilityCost ) );

						if( product.waterCost )
							hourlyUnitCoolingCost = hourlyUnitCoolingCost.add( new bigDecimal( product.waterCost ) );
					}

					// compute facility hourly carbon
					if( product.carbon ) {
						hourlyCarbon = hourlyCarbon.add( new bigDecimal( product.carbon ) );
					}
					else {
						const productCarbon = unitCarbonPerHour( product );
						hourlyCarbon = hourlyCarbon.add( new bigDecimal( productCarbon ) );
					}
				} );

				facility.capacity = facilityCapacity;

				// this is actually just combining power consumption of all units
				facility.powerUsedHourly = hourlyPowerUsed.getValue();
				facility.powerUsedDaily = hourlyPowerUsed.multiply( hoursPerDay ).getValue();
				facility.powerUsedWeekly = hourlyPowerUsed.multiply( hoursPerDay ).multiply( daysPerWeek ).getValue();
				facility.powerUsedAnnually = hourlyPowerUsed.multiply( hoursPerDay ).multiply( daysPerWeek ).multiply( new bigDecimal( 52 ) ).getValue();

				const carbonDailyByHourly = hourlyCarbon.multiply( hoursPerDay );
				const carbonDailyByEDaily = new bigDecimal( facility.powerUsedDaily ).multiply( new bigDecimal( 1.54 ) ).multiply( new bigDecimal( 0.454 ) );
				facility.carbonHourly = carbonDailyByHourly.getValue();
				facility.carbonDaily = carbonDailyByEDaily.getValue();
				facility.carbonWeekly = carbonDailyByEDaily.multiply( daysPerWeek ).getValue();
				facility.carbonAnnually = carbonDailyByEDaily.multiply( daysPerWeek ).multiply( new bigDecimal( 52 ) ).getValue();

				const totalHourlyCost = hourlyUnitPowerCost.add( hourlyFacilityCoolingCost ).add( hourlyUnitCoolingCost );
				facility.powerCostHourly = totalHourlyCost.round( 2, bigDecimal.RoundingModes.CEILING ).getValue();
				facility.powerCostDaily = totalHourlyCost.multiply( hoursPerDay ).getValue();
				facility.powerCostWeekly = totalHourlyCost.multiply( hoursPerDay ).multiply( daysPerWeek ).getValue();
				facility.powerCostAnnually = totalHourlyCost.multiply( hoursPerDay ).multiply( daysPerWeek ).multiply( new bigDecimal( 52 ) ).getValue();

				return facility;
			};

			/**
			 * calculate savings
			 */
			const calculateSavings = ( facility, recommendation ) => {
				recommendation.powerCostSavingsDaily = bigDecimal.subtract( facility.powerCostDaily, recommendation.powerCostDaily );
				recommendation.powerCostSavingsWeekly = bigDecimal.subtract( facility.powerCostWeekly, recommendation.powerCostWeekly );
				recommendation.powerCostSavingsAnnually = bigDecimal.subtract( facility.powerCostAnnually, recommendation.powerCostAnnually );

				recommendation.carbonSavingsDaily = bigDecimal.subtract( facility.carbonDaily, recommendation.carbonDaily );
				recommendation.carbonSavingsWeekly = bigDecimal.subtract( facility.carbonWeekly, recommendation.carbonWeekly );
				recommendation.carbonSavingsAnnually = bigDecimal.subtract( facility.carbonAnnually, recommendation.carbonAnnually );

				recommendation.powerUsedSavingsDaily = bigDecimal.subtract( facility.powerUsedDaily, recommendation.powerUsedDaily );
				recommendation.powerUsedSavingsWeekly = bigDecimal.subtract( facility.powerUsedWeekly, recommendation.powerUsedWeekly );
				recommendation.powerUsedSavingsAnnually = bigDecimal.subtract( facility.powerUsedAnnually, recommendation.powerUsedAnnually );
			};

			/**
			 * change current step
			*/
			const changeCurrentStep = ( dif ) => {
				currentStep.value += dif;
			};

			/**
			 * convert gallons to liters
			 */
			const convertGallonsToLiters = ( value ) => {
				return bigDecimal.multiply( value, '3.78541178' );
			};

			/**
				* deletes a unit from selected unit list
				*/
			const deleteSelectedUnit = ( index ) => {
				selectedUnits.value.splice( index, 1 );
			};

			/**
			 * format number to add commas/decimals for thousands, etc..
			 */
			const formatNumber = ( value, decimalPlaces = 0 ) => {
				if( !value ) return '';
				value = bigDecimal.round( value, decimalPlaces );
				return value.toLocaleString( undefined, { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces } );
			};

			/**
			 * format price by selected currency
			 */
			const formatPrice = ( value ) => {
				if( !value ) return '';
				const formatter = new Intl.NumberFormat( undefined, {
					currency: selectedCurrencyValue.value,
					style: 'currency',
				} );
				return formatter.format( bigDecimal.round( value, 2 ) );
			};

			/**
			 * print the page
			 */
			const printResults = () => {
				window.print();
			};

			/**
			 * find smallest unit to handle remaining capacity
			 */
			const recommendedProductByCapacity = ( capacity, latestModels ) => {
				const unitsToHandleRemainder = latestModels.filter( ( u ) => {
					const unitCapacity = 50 == selectedLocation.value ? u.max50 : u.max60;
					if( unitCapacity >= capacity ) {
						return u;
					}
				} );
				return unitsToHandleRemainder.sort( ( a, b ) => a.max60 - b.max60 )[0];
			};

			/**
			 * request quote
			 */
			const requestQuote = ( requestQuoteLink ) => {
				window.sessionStorage.removeItem( 'hpuSystem' );
				const formData = {
					system: selectedUnits.value.map( u => u.modelNumber ),
					electricityCost: basePowerRateValue.value,
					waterRateValue: waterRateValue.value,
					waterVolumeValue: waterVolumeValue.value,
					waterVolumeUnit: waterVolumeUnit.value,
					hoursPerDay: utilizationHoursValue.value,
					daysPerWeek: utilizationDaysValue.value,
					currency: selectedCurrencyValue.value,
					fpsm: fluidPowerSystemManaged.value,
					loc: selectedLocation.value,
					csv: costSliderValue.value,
					ssv: savingsSliderValue.value
				};
				formData.systemUrl = window.location.origin + window.location.pathname + '?' + $.param( formData );
				window.sessionStorage.hpuSystem = JSON.stringify( formData );
				window.location.href = requestQuoteLink;
			};

			/**
				* sets current step to a new number
				*/
			const setCurrentStep = ( step ) => {
				currentStep.value = step;
			};

			/**
			 * determine if mts has recommendations
			 */
			const setupRecommendations = () => {
				const hpu1 = selectedUnits.value[0].modelNumber;
				const latestModelFamily = data.modelFamilies.sort( ( a, b ) => b - a )[0];
				const latestModels = data.products.filter( ( p ) => latestModelFamily == p.modelFamily );
				const noncombinableModels = data.products.filter( ( product ) => !product.combinable ).map( ( p ) => p.modelNumber );
				const combinableLatestModels = latestModels.filter( ( product ) => product.combinable ).map( ( p ) => p.modelNumber );
				const noncombinableLatestModels = latestModels.filter( ( product ) => !product.combinable ).map( ( p ) => p.modelNumber );

				// recommendations exist if hpu1 is not a latest model
				const recommendationsExist = validateSelectedUnits( hpu1, [noncombinableLatestModels, combinableLatestModels] );
				// fluid power system section will be available if hpu1 is combinable and current system is not using it
				fluidPowerSystemSectionAvailable.value = validateSelectedUnits( hpu1, [noncombinableModels] );
				hasRecommendations.value = recommendationsExist;
				quoteAvailable.value = recommendationsExist;
				showFluidPowerSystemRecommendation.value = !facilityData.isFluidPowerSystemManaged && !validateSelectedUnits( hpu1, [combinableLatestModels] );

				if( !recommendationsExist && !showFluidPowerSystemRecommendation.value ) {
					return;
				} else if( 1440 < facilityData.capacity ) {
					showContactMessage.value = true;
					return;
				}

				// find the largest latest model
				const largestLatestModel = latestModels.sort( ( a, b ) => b.max60 - a.max60 )[0];
				const largestLatestModelCapacity = 50 == selectedLocation.value ? largestLatestModel.max50 : largestLatestModel.max60;

				// compare facility capacity to largest latest model's
				if( facilityData.capacity <= largestLatestModelCapacity ) {
					// we don't need more than one of the latest models
					// determine which latest model should be the recommendation
					const recommendedProduct = recommendedProductByCapacity( facilityData.capacity, latestModels );
					recommendedProducts.value = [{ quantity: 1, unit: recommendedProduct }];
				} else {
					// determine # of largest latest models
					// based on remainding capacity demands add other combinable units
					let numberLargestLatestUnits = Math.floor( facilityData.capacity / largestLatestModelCapacity );
					const remainder = facilityData.capacity - ( numberLargestLatestUnits * largestLatestModelCapacity );
					const combinableLatestUnits = latestModels.filter( ( m ) => -1 < combinableLatestModels.indexOf( m.modelNumber ) );
					const smallestUnitForRemainder = recommendedProductByCapacity( remainder, combinableLatestUnits );

					if( smallestUnitForRemainder.modelNumber === largestLatestModel.modelNumber ) {
						numberLargestLatestUnits += 1;
						recommendedProducts.value = [{ quantity: numberLargestLatestUnits, unit: largestLatestModel }];
					} else {
						recommendedProducts.value = [{ quantity: numberLargestLatestUnits, unit: largestLatestModel }, { quantity: 1, unit: smallestUnitForRemainder }];
					}
				}
				return recommendedProducts.value;
			};

			/**
			 *  calculate carbon kost per unit / hour
			 */
			const unitCarbonPerHour = ( unit ) => {
				const carbon = bigDecimal.multiply( unit.powerConsumed, bigDecimal.multiply( '1.54', '0.454' ) );
				unit.carbon = carbon;
				return carbon;
			};

			/**
			 * calculates unit water consumption per hour
			 */
			const unitWaterConsumedPerHour = ( unit ) => {
				let waterConsumed = unit.coolingGPM;
				if( 'l' == waterVolumeUnit.value ) {
					waterConsumed = convertGallonsToLiters( unit.coolingGPM );
				}
				unit.waterConsumed = bigDecimal.multiply( waterConsumed, 60 );
				return unit.waterConsumed;
			};

			/**
			 * calculate cost per unit / hour
			 */
			const unitCostPerHour = ( unit, isFPSM = false, useSavingsSlider = false  ) => {
				const unitPower = unitPowerConsumed( unit, isFPSM, useSavingsSlider );
				const unitWaterConsumed = unitWaterConsumedPerHour( unit );
				unit.unitPowerCost = bigDecimal.multiply( unitPower, basePowerRateValue.value );
				unit.waterCost = new bigDecimal( unitWaterConsumed ).multiply( new bigDecimal( waterRateValue.value ) ).divide( new bigDecimal( waterVolumeValue.value ) ).getValue();
				unit.facilityCost = bigDecimal.multiply( unit.heatLoad, basePowerRateValue.value );
				return unit;
			};

			/**
			 * determine max flow based on region [ 50Hz | 60Hz]
			 */
			const unitMaxFlow = ( unit ) => {
				let maxFlow = 0;
				if( 50 == selectedLocation.value )
					maxFlow = unit.max50;
				else
					maxFlow = unit.max60;

				unit.maxFlow = maxFlow;
				return maxFlow;
			};

			/**
			 * calculate unit power consumption
			 */
			const unitPowerConsumed = ( unit, isFPSM = false, useSavingsSlider = false ) => {
				isFPSM = isFPSM || facilityData.isFluidPowerSystemManaged;

				const c3 = new bigDecimal( isFPSM & 0 != unit.constantWith3 ? unit.constantWith3 : unit.constantWithout3 );
				const c4 = new bigDecimal( isFPSM & 0 != unit.constantWith4 ? unit.constantWith4 : unit.constantWithout4 );
				const c5 = new bigDecimal( isFPSM & 0 != unit.constantWith5 ? unit.constantWith5 : unit.constantWithout5 );
				const sliderValue = useSavingsSlider ? savingsSliderValue.value : costSliderValue.value;
				const maxFlow = unitMaxFlow( unit );
				const flowRate = isFPSM ? bigDecimal.multiply( sliderValue, 0.01 ) : 1;
				const oilFlow = new bigDecimal( bigDecimal.multiply( maxFlow, flowRate ) );
				// c3 + c4 * oilFlow + c5 * oilFlow²
				const c4Oil = c4.multiply( oilFlow );
				const c5Oil = c5.multiply( oilFlow.multiply( oilFlow ) );
				const unitPower = c3.add( c4Oil ).add( c5Oil ).getValue();
				unit.powerConsumed = unitPower;
				unitCarbonPerHour( unit );
				return unitPower;
			};

			/**
			 * see if hpu model number is in first list
			 * if second list provided make sure all selected units model numbers are in second list
			 * modelNumber = string
			 * lists = string[]
			 */
			const validateSelectedUnits = ( modelNumber, lists ) => {
				if( -1 < lists[0].indexOf( modelNumber ) )
					return false;
				else if ( lists[1] && facilityData.isFluidPowerSystemManaged && selectedUnits.value.every( ( u ) => -1 < lists[1].indexOf( u.modelNumber ) ) )
					return false;

				return true;
			};

			/**
			 * watch for change in cost variables and update unit cost
			 */
			watch( [basePowerRateValue, waterRateValue, waterVolumeValue], () => {
				selectedProducts.value.forEach( ( product ) => {
					unitCostPerHour( product );
				} );
			} );

			/**
			 * map selected currency value to value of currency select fields
			 */
			watch( selectedCurrency, ( newCurrency ) => {
				selectedCurrencyValue.value = newCurrency;
			} );

			/**
			 * update facility fluid system managed bool if fluid power system managed changed
			 */
			watch( fluidPowerSystemManaged, ( systemManaged ) => {
				facilityData.isFluidPowerSystemManaged = 'yes' === systemManaged;
				selectedProducts.value.forEach( ( product ) => {
					unitCostPerHour( product );
				} );
			} );

			/**
			 * update selected product max flow if region changes
			 */
			watch( selectedLocation, () => {
				selectedProducts.value.forEach( ( product ) => {
					unitCostPerHour( product );
				} );
			} );

			/**
			 * watch for change in selected units and update unit cost & carbon
			 */
			watch( selectedUnits, () => {
				if( 0 < selectedUnits.value.length ) {
					selectedProducts.value = [];
					selectedUnits.value.forEach( ( unit ) => {
						// this logic will check the model number is valid for the selected model family
						// if it's not it selects the first available unit in that model family
						const family = unit.modelFamily;
						const modelNumber = unit.modelNumber;
						if( family != modelNumber.split( '.' )[0] ) {
							const productsInFamily = data.productsByModelFamily[family];
							unit.modelNumber = productsInFamily[0].modelNumber;
						}

						// updated selected products
						const product = data.products.find( ( p ) => p.modelNumber == unit.modelNumber );
						if( 0 === product.maxFlow )
							unitMaxFlow( product );
						if( 0 === product.waterConsumed )
							unitWaterConsumedPerHour( product );
						if( 0 === product.unitPowerCost || 0 === product.waterCost || 0 === product.facilityCost )
							unitCostPerHour( product );
						if( 0 === product.carbon )
							unitCarbonPerHour( product );
						selectedProducts.value.push( product );

						// logic to determine if the add unit button is available
						if( !product.combinable )
							displayAddUnitButton.value = false;
						else if( 7 < selectedUnits.value.length )
							displayAddUnitButton.value = false;
						else if ( !displayAddUnitButton.value )
							displayAddUnitButton.value = true;

						if( product.combinable ) {
							availableModelFamilies.value = new Set( data.products.filter( ( product => product.combinable ) ).map( ( product ) => product.modelFamily ) );
						}
					} );
				}
			}, { deep: true } ); // adding deep allows the watch to detect when units are added/removed

			/**
			 * watch cost slider value
			 */
			watch( costSliderValue, () => {
				if ( facilityData.isFluidPowerSystemManaged ) {
					selectedProducts.value.forEach( ( product ) => {
						unitCostPerHour( product );
					} );
					calculateFacilityData( facilityData, selectedProducts.value );
				}

				if( 0 > recommendedFacilityProducts.value.length )
					return;

				recommendedFacilityProducts.value.forEach( ( product ) => {
					unitCostPerHour( product, true, true );
				} );
				const fd = calculateFacilityData( recommendationFacilityFMSData, recommendedFacilityProducts.value, true );
				calculateSavings( facilityData, fd );
			} );

			/**
			 * watch savings slider value
			 */
			watch( savingsSliderValue, () => {
				if( 0 > recommendedFacilityProducts.value.length )
					return;

				recommendedFacilityProducts.value.forEach( ( product ) => {
					unitCostPerHour( product, true, true );
				} );
				const fd = calculateFacilityData( recommendationFacilityFMSData, recommendedFacilityProducts.value, true );
				calculateSavings( facilityData, fd );
			} );

			/**
			 * update water consumption
			 */
			watch( waterVolumeUnit, () => {
				selectedProducts.value.forEach( ( product ) => {
					unitWaterConsumedPerHour( product );
					unitCostPerHour( product );
				} );
			} );

			/**
				* watch for step 3, then calculate facility data and recommendations
				*/
			watch( currentStep, () => {
				if( 3 === currentStep.value ) {
					calculateFacilityData( facilityData, selectedProducts.value );
					setupRecommendations();
				}
			} );

			/**
			 * watch for when recommended products change
			 */
			watch( recommendedProducts, ( recommendations ) => {
				let recommendedProductList = [];
				recommendations.forEach( ( recommendation ) => {
					if( 1 === recommendation.quantity ) {
						recommendedProductList.push( recommendation.unit );
					} else {
						const multiProduct = Array( recommendation.quantity ).fill( recommendation.unit );
						recommendedProductList = recommendedProductList.concat( multiProduct );
					}
				} );

				if( true == fluidPowerSystemSectionAvailable.value ) {
					recommendationFacilityFMSData.isFluidPowerSystemManaged = true;
					recommendedProductList.forEach( ( product ) => {
						unitCostPerHour( product, true, true );
					} );
					recommendedFacilityProducts.value = recommendedProductList;
					const recFMSFacility = calculateFacilityData( recommendationFacilityFMSData, recommendedProductList, true );
					calculateSavings( facilityData, recFMSFacility );
				} else {
					recommendationFacilityData.name = 'recommendations facility but from watch';
					const recFacility = calculateFacilityData( recommendationFacilityData, recommendedProductList );
					calculateSavings( facilityData, recFacility );
				}
			} );

			/**
			 * wait until initialization, then set current step so we can trigger the watch
			 */
			if ( urlParamsHasSystem ) {
				currentStep.value = 3;
			}

			return {
				addNewUnit,
				availableModelFamilies,
				basePowerRateValue,
				changeCurrentStep,
				combinableProductsByModelFamily: data.combinableProductsByModelFamily,
				costSliderValue,
				currencies: data.currencies,
				currentStep,
				deleteSelectedUnit,
				displayAddUnitButton,
				facilityData,
				fluidPowerSystemManaged,
				fluidPowerSystemSectionAvailable,
				formatNumber,
				formatPrice,
				gallonsText,
				hasRecommendations,
				litersText,
				locations: data.locations,
				modelFamilies: data.modelFamilies,
				printResults,
				products: data.products,
				productsByModelFamily: data.productsByModelFamily,
				quoteAvailable,
				recommendationFacilityData,
				recommendationFacilityFMSData,
				recommendedProducts,
				requestQuote,
				savingsSliderValue,
				selectedCurrency,
				selectedCurrencyValue,
				selectedLocation,
				selectedProducts,
				selectedUnits,
				setCurrentStep,
				showContactMessage,
				showFluidPowerSystemRecommendation,
				utilizationDaysValue,
				utilizationHoursValue,
				volumeUnits: data.volumeUnits,
				waterRateValue,
				waterVolumeUnit,
				waterVolumeValue
			};
		},
	} );
	app.mount( calculator );
}

function populateForm( form, facilitySessionData ) {
	addHiddenField( form, 'daysPerWeek', facilitySessionData.daysPerWeek );
	addHiddenField( form, 'electricityCost', facilitySessionData.electricityCost );
	addHiddenField( form, 'hoursPerDay', facilitySessionData.hoursPerDay );
	addHiddenField( form, 'waterRateValue', facilitySessionData.waterRateValue );
	addHiddenField( form, 'waterVolumeUnit', facilitySessionData.waterVolumeUnit );
	addHiddenField( form, 'waterVolumeValue', facilitySessionData.waterVolumeValue );
	addHiddenField( form, 'systemUrl', facilitySessionData.systemUrl );
	addHiddenField( form, 'currency', facilitySessionData.currency );
	addHiddenField( form, 'fpsm', facilitySessionData.fpsm );
	addHiddenField( form, 'loc', facilitySessionData.loc );
	addHiddenField( form, 'csv', facilitySessionData.csv );
	addHiddenField( form, 'ssv', facilitySessionData.ssv );
	const system = facilitySessionData.system;
	system.map( ( hpu, i ) => addHiddenField( form, `hpu-${i + 1}`, hpu ) );
}
